<template>
  <div class="d-flex flex-row justify-content-center align-items-center">
    <mdb-btn-group>
      <mdb-btn
        v-if="isManager"
        rounded
        size="sm"
        icon="search"
        gradient="aqua"
        title="Search for entities"
        @click="modal.search = true"
      />
      <mdb-btn
        v-if="isAdmin"
        rounded
        size="sm"
        icon="search-plus"
        gradient="aqua"
        title="Create new search template"
        @click="modal.create = true"
      />
    </mdb-btn-group>
    <mdb-modal size="lg" :show="modal.create" @close="modal.create = false">
      <mdb-modal-header class="aqua-gradient">
        <mdb-modal-title>Create New Search Template</mdb-modal-title>
      </mdb-modal-header>
      <mdb-modal-body>
        <mdb-stepper
          class="pt-1"
          buttons
          validation
          :validatedSteps="create.validatedSteps"
          @validate="validate"
          @submit="submit"
          :steps="create.steps"
          :options="create.options"
        >
          <template v-slot:1>
            <h5 class="font-weight-bold pt-3 pl-0 my-4">
              Cypher Template
            </h5>
            <form ref="form1">
              <mdb-input type="text" label="Name" v-model="create.data.name" />
              <mdb-textarea rows="5" label="Template" v-model="create.data.template" />
            </form>
          </template>
          <template v-slot:2>
            <h5 class="font-weight-bold pt-3 pl-0 my-4">
              Variables
            </h5>
            <form ref="form2">
              <mdb-container
                class="border rounded m-2"
                v-for="(variable, index) in create.data.variables"
                :key="`variable-${index}-container`"
              >
                <mdb-row>
                  <mdb-col col="4" class="m-auto"><h6>{{ variable.code }}</h6></mdb-col>
                  <mdb-col col="4">
                    <mdb-input size="sm" type="text" label="Name" v-model="variable.name" />
                  </mdb-col>
                  <mdb-col col="4">
                    <mdb-select
                      :disabled="!!variable.suggestion"
                      size="sm"
                      label="Type"
                      v-model="create.types[index]"
                      @getValue="onTypeChange(index, $event)"
                    />
                  </mdb-col>
                </mdb-row>
                <mdb-row>
                  <mdb-col col="4" class="d-flex align-content-center">
                    <mdb-switch
                      size="sm"
                      color="cyan"
                      off-label="Free"
                      on-label="Suggested"
                      @getValue="onSuggestedChange(index, $event)"
                    />
                  </mdb-col>
                  <mdb-col col="7">
                    <span v-if="!!variable.suggestion">
                      {{ variable.suggestion ? `${variable.suggestion.label} / ${variable.suggestion.prop}` : '' }}
                    </span>
                  </mdb-col>
                  <mdb-col col="1">
                    <a
                      v-if="!!variable.suggestion"
                      @click="onSuggestionClicked(index)"
                    >
                      <mdb-icon icon="ellipsis-v" />
                    </a>
                  </mdb-col>
                </mdb-row>
              </mdb-container>
            </form>
          </template>
          <template v-slot:3>
            <h5 class="font-weight-bold pt-3 pl-0 my-4">
              Check Configuration
            </h5>
            <mdb-tbl>
              <mdb-tbl-body>
                <tr>
                  <th>Name</th>
                  <td>{{ create.data.name }}</td>
                </tr>
                <tr>
                  <th>Template</th>
                  <td>{{ create.data.template }}</td>
                </tr>
                <template v-for="(variable, index) in create.data.variables">
                  <tr :key="`var-${index}-1`">
                    <th :rowspan="variable.suggestion ? '3' : '2'">{{ variable.code}}</th>
                    <th>Name</th>
                    <td>{{ variable.name }}</td>
                  </tr>
                  <tr :key="`var-${index}-2`">
                    <th>Type</th>
                    <td>{{ variable.type }}</td>
                  </tr>
                  <tr v-if="!!variable.suggestion" :key="`var-${index}-3`">
                    <th>Suggestion</th>
                    <td>{{ variable.suggestion && `${variable.suggestion.label} / ${variable.suggestion.prop}` }}</td>
                  </tr>
                </template>
              </mdb-tbl-body>
            </mdb-tbl>
          </template>
        </mdb-stepper>
      </mdb-modal-body>
    </mdb-modal>
    <mdb-modal size="lg" :show="modal.search" @close="closeSearch">
      <mdb-modal-header class="aqua-gradient">
        <mdb-modal-title>Search</mdb-modal-title>
        <mdb-select
          size="sm"
          class="w-100"
          v-model="search.templateOptions"
          @getValue="onTemplateSelected($event)"
        />
      </mdb-modal-header>
      <mdb-modal-body>
        <mdb-stepper v-if="search.template" simpleH :steps="search.template.variables.length + 1">
          <template
            v-for="(variable, index) in search.template && search.template.variables"
            :slot="index + 1"
          >
            <div :key="`search-var-${index}`">
              <bridge8-select-list
                v-if="!!variable.suggestion"
                class="my-3 mx-auto w-75"
                entity="label"
                :options="search.suggestions[index]"
                :label="variable.name"
                :search="true"
                @selected="$set(search.variables, index, $event)"
              />
              <mdb-input
                v-else
                class="my-3 mx-auto w-75"
                type="text"
                :label="variable.name"
                v-model="search.variables[index]"
                @change="onVariableChange(variable.type, index, $event)"
              />
            </div>
          </template>
          <template :slot="search.template.variables.length + 1">
            <h5>Check variables</h5>
            <mdb-tbl>
              <mdb-tbl-body>
                <tr
                  v-for="(variable, index) in search.variables"
                  :key="`variables-table-row-${index}`"
                >
                  <th>{{ search.template.variables[index].name }}</th>
                  <td>{{ variable }}</td>
                </tr>
              </mdb-tbl-body>
            </mdb-tbl>
            <mdb-btn
              :disabled="search.variables.some(v => v === null || v === undefined)"
              size="sm"
              rounded
              gradient="aqua"
              icon="search"
              @click="onSearchClicked"
            >Search</mdb-btn>
            <span
              v-if="Array.isArray(search.results) && search.results.length === 0"
              class="text-danger"
            >Unfortunately, nothing found...</span>
            <div v-else-if="Array.isArray(search.results)">
              <span>Found {{ search.results.length }} element{{ search.results.length > 1 ? 's' : '' }}</span>
              <mdb-btn
                size="sm"
                rounded
                gradient="aqua"
                icon="cart-plus"
                @click="onAddToCartClicked"
              >Add to cart</mdb-btn>
            </div>
          </template>
        </mdb-stepper>
      </mdb-modal-body>
    </mdb-modal>
  </div>
</template>

<script>
import {
  mdbBtn,
  mdbBtnGroup,
  mdbInput,
  mdbSelect,
  mdbTextarea,
  mdbModal,
  mdbModalBody,
  mdbModalHeader,
  mdbModalTitle,
  mdbModalFooter,
  mdbStepper,
  mdbRow,
  mdbCol,
  mdbContainer,
  mdbSwitch,
  mdbIcon,
  mdbTbl,
  mdbTblHead,
  mdbTblBody
} from 'mdbvue'
import {mapGetters, mapActions} from 'vuex'
import {InternalPropertyDataTypes, InternalPropertyDataTypesHumanReadable} from '../enums'
import {errorCatch} from '../utils'
import Bridge8SelectList from './Bridge8SelectList'

export default {
  name: 'AdminTabSearch',
  data () {
    return {
      modal: {
        search: false,
        create: false
      },
      search: {
        templates: [],
        templateOptions: [],
        template: null,
        variables: [],
        suggestions: [],
        results: []
      },
      create: {
        data: {
          name: null,
          template: null,
          variables: []
        },
        suggestions: [],
        types: [],
        steps: [
          { icon: 'pencil-alt', name: 'Template' },
          { icon: 'list', name: 'Variables' },
          { icon: 'check', name: 'Finish' }
        ],
        options: {
          stepBtn: {
            outline: 'cyan',
            active: 'cyan',
            iconClass: 'cyan-text'
          },
          nextBtn: {
            outline: 'cyan'
          },
          prevBtn: {
            outline: 'cyan'
          },
          submitBtn: {
            text: 'Create',
            color: 'cyan'
          },
          lineColor: 'cyan'
        },
        validatedSteps: {}
      }
    }
  },
  computed: {
    ...mapGetters('user', ['isUser', 'isManager', 'isAdmin'])
  },
  methods: {
    ...mapActions([
      'apiSearchTemplateCreate',
      'apiSearchTemplateDelete',
      'apiSearchTemplatesGet',
      'apiSearchTemplate',
      'apiGetPropValues'
    ]),
    closeSearch () {
      this.$set(this.modal, 'search', false)
      this.onTemplateSelected(null)
    },
    getSuggestions () {
      const len = this.search.template.variables.length
      this.$set(this.search, 'suggestions', new Array(len).fill([]))
      this.search.template.variables.forEach((variable, index) => {
        if (variable.suggestion) {
          this.apiGetPropValues(variable.suggestion)
            .then(values => {
              this.$set(this.search.suggestions, index, values)
            })
        }
      })
    },
    onTemplateSelected (uuid) {
      if (uuid === null) {
        this.$set(this.search, 'template', null)
        this.resetTemplateOptions()
        this.$set(this.search, 'variables', [])
        this.$set(this.search, 'suggestions', [])
      } else {
        this.$set(this.search, 'template', this.search.templates.find(template => template.uuid === uuid))
        this.getSuggestions()
      }
      this.$set(this.search, 'results', null)
    },
    resetTemplateOptions () {
      this.$set(
        this.search,
        'templateOptions',
        this.search.templates.map(template => {
          return {
            value: template.uuid,
            text: template.name,
            selected: template.uuid === this.search.template && this.search.template.uuid
          }
        })
      )
    },
    onVariableChange (type, index, value) {
      if (type === InternalPropertyDataTypes.NUMBER) {
        this.$set(this.search.variables, index, parseFloat(value))
      }
    },
    onSearchClicked () {
      this.apiSearchTemplate({
        uuid: this.search.template.uuid,
        vars: this.search.variables
      })
        .then(results => {
          this.search.results = results
        })
        .catch(errorCatch('Template Search failed', this))
    },
    onAddToCartClicked () {
      this.search.results.forEach(result => {
        this.$bus.$emit('admin-cart-add-node', result)
      })
      this.closeSearch()
    },
    onTypeChange (index, value) {
      this.$set(this.create.data.variables[index], 'type', value)
    },
    onSuggestedChange (index, value) {
      if (value === true) {
        this.$bus.$emit('admin-modal-select-prop', (label, prop) => {
          this.$set(this.create.data.variables[index], 'suggestion', {label: label, prop: prop})
          this.$set(this.create.data.variables[index], 'type', InternalPropertyDataTypes.STRING)
          this.calcTypes()
        })
      } else {
        this.$set(this.create.data.variables[index], 'suggestion', null)
      }
    },
    onSuggestionClicked (index) {
      this.$bus.$emit('admin-modal-select-prop', (label, prop) => {
        this.$set(this.create.data.variables[index], 'suggestion', {label: label, prop: prop})
      })
    },
    validate (step) {
      // this.$refs[`form${step}`].classList.add('was-validated')

      switch (step) {
        case 1:
          const nameType = typeof this.create.data.name === 'string'
          const nameLength = this.create.data.name.length > 0
          const templateType = typeof this.create.data.template === 'string'
          const templateLength = this.create.data.template.length > 0

          const vars = this.create.data.template.match(/(\$[a-z][a-zA-Z0-9]*)/g) || []
          const obj = {}
          vars.forEach(v => {
            obj[v] = this.create.data.variables[v] || {
              code: v,
              name: null,
              type: null,
              suggestion: null
            }
          })
          const noDuplicates = vars.length === Object.keys(obj).length

          this.create.validatedSteps[1] =
            nameType && nameLength && templateType && templateLength && noDuplicates
          if (this.create.validatedSteps[1]) {
            this.$set(this.create.data, 'variables', Object.values(obj))
            this.calcTypes()
          }
          break
        case 2:
          this.create.validatedSteps[2] = this.create.data.variables.every(value => {
            return typeof value.code === 'string' && value.code.length > 1 &&
              value.code[0] === '$' &&
              typeof value.name === 'string' && value.name.length > 0 &&
              ['STRING', 'NUMBER'].includes(value.type) &&
              (
                value.suggestion === null ||
                (
                  value.type === 'STRING' &&
                    typeof value.suggestion.label === 'string' &&
                    value.suggestion.label.length > 0 &&
                    typeof value.suggestion.prop === 'string' &&
                    value.suggestion.prop.length > 0
                )
              )
          })
      }
    },
    calcTypes () {
      this.$set(this.create, 'types',
        this.create.data.variables.map(v => {
          return [
            {
              value: InternalPropertyDataTypes.STRING,
              text: InternalPropertyDataTypesHumanReadable[InternalPropertyDataTypes.STRING],
              selected: v.type === InternalPropertyDataTypes.STRING
            },
            {
              value: InternalPropertyDataTypes.NUMBER,
              text: InternalPropertyDataTypesHumanReadable[InternalPropertyDataTypes.NUMBER],
              selected: v.type === InternalPropertyDataTypes.NUMBER
            }
          ]
        })
      )
    },
    submit () {
      this.apiSearchTemplateCreate(this.create.data)
        .then(uuid => {
          this.$set(
            this.create,
            'data',
            {
              name: null,
              template: null,
              variables: []
            }
          )
          this.$notify.success({
            title: 'Search Template Created.',
            message: `UUID: ${uuid}`,
            position: 'top right',
            timeOut: 10000,
            closeButton: true
          })
          this.apiSearchTemplatesGet()
            .then(templates => {
              this.$set(this.search, 'templates', templates)
            })
            .catch(errorCatch('Get Search Templates failed after creating new one'))
        })
        .catch(errorCatch('Search Template Creation Failed', this))
    }
  },
  mounted () {
    this.apiSearchTemplatesGet()
      .then(templates => {
        this.search.templates = templates
      })
      .catch(errorCatch('Get Search Templates failed'))
  },
  watch: {
    'search.templates': {
      handler (newVal, oldVal) {
        this.resetTemplateOptions()
      },
      deep: true
    }
  },
  components: {
    Bridge8SelectList,
    mdbBtn,
    mdbBtnGroup,
    mdbInput,
    mdbSelect,
    mdbTextarea,
    mdbModal,
    mdbModalBody,
    mdbModalHeader,
    mdbModalTitle,
    mdbModalFooter,
    mdbStepper,
    mdbRow,
    mdbCol,
    mdbContainer,
    mdbSwitch,
    mdbIcon,
    mdbTbl,
    mdbTblHead,
    mdbTblBody
  }
}
</script>

<style lang="sass" scoped>
.modal-body
  max-height: none!important
</style>



// WEBPACK FOOTER //
// src/components/AdminTabSearch.vue